import { get, post } from '../apiClient';
import type {
  Migration,
  MigrationStartedResponse,
  MigrationSummary,
  MigrationLog,
  PaginatedList,
  StartMigrationRequest,
  RetryMigrationRequest,
  AttachOrganizationRequest,
  AttachOrganizationResult,
} from '@/types';

export interface MigrationsFilters {
  pageNumber?: number | undefined;
  pageSize?: number | undefined;
  /** Comma-separated list of statuses; the API parses this into a set. */
  status?: string | undefined;
  /** Free-text search over organization name + started-by user. Maps to the API's `searchTerm`. */
  searchTerm?: string | undefined;
  /** Inclusive lower bound on StartedAt, ISO-8601. */
  startDateFrom?: string | undefined;
  /** Inclusive upper bound on StartedAt, ISO-8601 (end-of-day). */
  startDateTo?: string | undefined;
  sortBy?: string | undefined;
  sortDesc?: boolean | undefined;
}

export const migrationsApi = {
  list: (filters: MigrationsFilters = {}) =>
    get<PaginatedList<MigrationSummary>>('/migrations', filters),

  get: (id: string) => get<Migration>(`/migrations/${id}`),

  start: (data: StartMigrationRequest) =>
    post<MigrationStartedResponse>('/migrations', data),

  retry: (id: string, data: RetryMigrationRequest = {}) =>
    post<Migration>(`/migrations/${id}/retry`, data),

  retryUserMigration: (id: string) =>
    post<{ jobId: string }>(`/migrations/${id}/retry-user-migration`),

  forceFail: (id: string) =>
    post<void>(`/migrations/${id}/force-fail`),

  attachOrganization: (id: string, data: AttachOrganizationRequest) =>
    post<AttachOrganizationResult>(`/migrations/${id}/attach-organization`, data),

  getLogs: (id: string) =>
    get<PaginatedList<MigrationLog>>(`/migrations/${id}/logs`, { pageSize: 200 }),
};

// Query key factory for TanStack Query
export const migrationKeys = {
  all: ['migrations'] as const,
  lists: () => [...migrationKeys.all, 'list'] as const,
  list: (filters: MigrationsFilters) => [...migrationKeys.lists(), filters] as const,
  details: () => [...migrationKeys.all, 'detail'] as const,
  detail: (id: string) => [...migrationKeys.details(), id] as const,
  logs: (id: string) => [...migrationKeys.detail(id), 'logs'] as const,
  logsFiltered: (id: string, moduleKey?: string) =>
    [...migrationKeys.logs(id), { moduleKey }] as const,
};
